import React, { useContext, useEffect, useState } from "react";
import Container from "react-bootstrap/Container";
import { Link } from "react-router-dom";
import Button from "../../../components/atoms/Buttons/Button/Button";
import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import Headline from "../../../components/organisms/Headline/Headline";
import Loader from "../../../components/atoms/Loader/Loader";
import FullWidthCard, {
  CardContentItem,
} from "../../../components/organisms/Cards/full-width-card/FullWidthCard";
import SearchHelper from "../../../components/organisms/Cards/user-card-controller/SearchHelper";
import Pagination from "../../../components/molecules/Pagination/Pagination";
import FilterSection from "../../user-management/components/FilterSection";
import cardContent from "../../../utils/helpers/helperDataset/organisation-management-contents/cardContent.json";
import { TodosContext } from "../../../context/alert";
import useUserRole from "../../../hooks/useUserRole";
import { UserRole } from "../../../utils";
import { OrganisationService } from "../service/organisationService";
import { IOrganisation, IOrganisationsResponse } from "../models/IOrganisation";
import { getCountryName, ORGANISATIONS_PAGE_SIZE } from "../constants";

const SEARCH_DEBOUNCE_MS = 300;

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

// Shapes an organisation into the flat fields FullWidthCard renders.
const toCardData = (organisation: IOrganisation): any => ({
  id: organisation.id,
  email: organisation.primaryContactEmail,
  code: organisation.code,
  subscriptionPlan: organisation.subscriptionPlan?.planName,
  country: getCountryName(organisation.countryId),
  primaryContactName:
    `${organisation.primaryContactFirstName} ${organisation.primaryContactLastName}`.trim(),
  primaryContactPhone: organisation.primaryContactPhone,
  createdAt: organisation.createdAt,
  note: organisation.note,
});

const ListOfOrganisations = () => {
  const { addAlert } = useContext(TodosContext);
  const [organisations, setOrganisations] = useState<IOrganisationsResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const isGlobalAdmin = useUserRole() === UserRole.GlobalAdmin;

  useEffect(() => {
    let isCancelled = false;
    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await OrganisationService.getOrganisations({
          pageNumber,
          pageSize: ORGANISATIONS_PAGE_SIZE,
          searchText,
        });
        if (!isCancelled) setOrganisations(response);
      } catch (e) {
        if (!isCancelled) addAlert("Failed to load organisations", "failed", true);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    }, searchText ? SEARCH_DEBOUNCE_MS : 0);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, searchText, reloadKey]);

  const handleSetFilteredValue = (inputValue: string) => {
    setPageNumber(1);
    setSearchText(inputValue);
  };

  const handleDeleteOrganisation = async (organisation: any) => {
    setIsLoading(true);
    try {
      await OrganisationService.deleteOrganisation(organisation.id);
      addAlert("Organisation Deleted Successfully", "success", true);
      // Step back a page when the last card on a page is deleted.
      if (pageNumber > 1 && organisations?.data.length === 1) {
        setPageNumber(pageNumber - 1);
      } else {
        setReloadKey((key) => key + 1);
      }
    } catch (e: any) {
      setIsLoading(false);
      addAlert(e?.message || "Organisation Delete Failed", "failed", true);
    }
  };

  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />

      <Headline
        name="Organisation"
        subHeading="Add, edit, and manage organisation"
      />

      <div className="content-wrapper">
        <div className="filter-wrapper">
          <FilterSection
            handleSetFilteredValue={handleSetFilteredValue}
            placeholder="Search by organisation name, code or anything"
          />

          <Link to="/organisations/add-organisation">
            <Button cssClass="btn-primary" text="Add New Organisation" />
          </Link>
        </div>
      </div>

      <div className="content-wrapper">
        {isLoading ? (
          <Loader />
        ) : organisations?.data && organisations.data.length > 0 ? (
          organisations.data.map((organisation) => (
            <FullWidthCard
              key={organisation.id}
              cardContent={cardContent as CardContentItem[]}
              data={toCardData(organisation)}
              title={organisation.name}
              avatarText={getInitials(organisation.name)}
              emailLabel="Primary Contact Email"
              pathUrl="organisations"
              deleteCard={handleDeleteOrganisation}
              isShowAction={isGlobalAdmin}
            />
          ))
        ) : (
          <SearchHelper
            userdata={organisations}
            isLoading={isLoading}
            filteredValue={[]}
            status=""
            clickFilterSearch
          />
        )}

        {organisations && organisations.totalPages > 1 ? (
          <Pagination
            pageNumber={pageNumber}
            onPageChange={setPageNumber}
            totalPages={organisations.totalPages}
            pageSize={ORGANISATIONS_PAGE_SIZE}
            totalRecords={organisations.totalRecords}
            label="Organisations"
          />
        ) : null}
      </div>
    </Container>
  );
};

export default ListOfOrganisations;
